/**
 * 🐦‍⬛ LCW Ritual Sticky — v1.3
 * - Mede altura do heading e seta --rs-heading-height
 * - Marca step como .is-active / .is-collapsed
 * - Click no step colapsado rola de volta pra revelar
 * - Click no vídeo abre fullscreen (iOS Safari + Fullscreen API)
 * - Autoplay muted loop com fallback pro botão play
 */

class LcwRitualSticky {
  constructor(root) {
    this.root = root;
    this.MOBILE_BREAKPOINT = 749;

    this.steps = Array.from(root.querySelectorAll('.lcw-rs__step'));
    this.heading = root.querySelector('.lcw-ritual-sticky__heading');
    this.footer = root.querySelector('.lcw-ritual-sticky__footer');
    this.media = root.querySelector('.lcw-ritual-sticky__media');
    this.mediaWrap = root.querySelector('.lcw-ritual-sticky__media-wrap');
    this.video = root.querySelector('.lcw-ritual-sticky__video');
    this.poster = root.querySelector('.lcw-ritual-sticky__poster');
    this.playBtn = root.querySelector('.lcw-ritual-sticky__play');

    this.resizeHandler = this.onResize.bind(this);
    this.scrollHandler = this.onScroll.bind(this);
    this.scrollTicking = false;

    this.init();
  }

  init() {
    this.setupSteps();
    this.setupVideo();
    this.setupVideoFullscreen();
    this.measureHeading();
    this.setFooterMinHeight();
    this.updateAllStates();

    window.addEventListener('resize', this.resizeHandler);
    window.addEventListener('orientationchange', this.resizeHandler);
    window.addEventListener('scroll', this.scrollHandler, { passive: true });
  }

  isMobile() {
    return window.innerWidth <= this.MOBILE_BREAKPOINT;
  }

  /* ============================================
     HEADING measurement
     ============================================ */

  measureHeading() {
    if (!this.heading) {
      this.root.style.setProperty('--rs-heading-height', '0px');
      return;
    }
    const h = this.heading.offsetHeight;
    this.root.style.setProperty('--rs-heading-height', `${h}px`);
  }

  /* ============================================
     FOOTER min-height (mobile)
     Set footer.min-height = max step height. Assim quando o container `.steps`
     está terminando, o step 05 pushed é posicionado exatamente onde o footer.top
     está — o footer cobre o step 05 pelo z-index, o step não fica visível "subindo".
     ============================================ */

  setFooterMinHeight() {
    if (!this.footer) return;

    if (!this.isMobile()) {
      this.footer.style.minHeight = '';
      return;
    }

    if (this.steps.length === 0) {
      this.footer.style.minHeight = '';
      return;
    }

    // Mede a maior altura entre os steps (pra ser safe caso variem)
    const maxStepHeight = Math.max(
      ...this.steps.map((s) => s.offsetHeight)
    );

    // Adiciona pequeno buffer pra folga
    this.footer.style.minHeight = `${maxStepHeight + 16}px`;
  }

  onResize() {
    this.measureHeading();
    this.setFooterMinHeight();
    this.updateAllStates();
  }

  /* ============================================
     SCROLL: throttled com rAF
     ============================================ */

  onScroll() {
    if (this.scrollTicking) return;
    this.scrollTicking = true;
    requestAnimationFrame(() => {
      this.updateAllStates();
      this.scrollTicking = false;
    });
  }

  updateAllStates() {
    this.updateStepStates();
  }

  /* ============================================
     STEP STATES: is-active / is-collapsed
     ============================================ */

  setupSteps() {
    this.steps.forEach((step) => {
      step.addEventListener('click', (e) => this.onStepClick(e, step));
    });
  }

  updateStepStates() {
    if (!this.isMobile()) {
      this.steps.forEach((s) => s.classList.remove('is-active', 'is-collapsed'));
      return;
    }

    this.steps.forEach((step, i) => {
      const stickyTop = this.getStickyTopPx(step);
      const rect = step.getBoundingClientRect();
      const isStuck = rect.top <= stickyTop + 1;

      let isNextStuck = false;
      if (i < this.steps.length - 1) {
        const next = this.steps[i + 1];
        const nextStickyTop = this.getStickyTopPx(next);
        const nextRect = next.getBoundingClientRect();
        isNextStuck = nextRect.top <= nextStickyTop + 1;
      }

      const isActive = isStuck && !isNextStuck;
      const isCollapsed = isStuck && isNextStuck;

      step.classList.toggle('is-active', isActive);
      step.classList.toggle('is-collapsed', isCollapsed);
    });
  }

  getStickyTopPx(step) {
    const computed = window.getComputedStyle(step);
    return parseFloat(computed.top) || 0;
  }

  onStepClick(e, step) {
    if (!this.isMobile()) return;
    if (e.target.closest('a, button, form, input, [role="button"]')) return;
    if (!step.classList.contains('is-collapsed')) return;

    e.preventDefault();

    const stickyTop = this.getStickyTopPx(step);
    const rect = step.getBoundingClientRect();
    const naturalTop = rect.top + window.scrollY;
    const target = Math.max(0, naturalTop - stickyTop - 8);

    window.scrollTo({
      top: target,
      behavior: 'smooth',
    });
  }

  /* ============================================
     VIDEO: autoplay muted loop
     ============================================ */

  setupVideo() {
    if (!this.video) return;

    // Redundância pra iOS (atributos + propriedades)
    this.video.muted = true;
    this.video.loop = true;
    this.video.playsInline = true;
    this.video.autoplay = true;

    const markReady = () => {
      this.root.classList.add('is-video-ready');
      if (this.playBtn) this.playBtn.hidden = true;
    };

    const tryPlay = () => {
      const p = this.video.play();
      if (p && typeof p.then === 'function') {
        p.then(markReady).catch(() => {
          // Autoplay bloqueado (iOS Low Power) — mostra botão play
          if (this.playBtn) this.playBtn.hidden = false;
        });
      } else {
        markReady();
      }
    };

    if (this.video.readyState >= 2) {
      tryPlay();
    } else {
      this.video.addEventListener('loadedmetadata', tryPlay, { once: true });
      this.video.addEventListener('loadeddata', tryPlay, { once: true });
      this.video.addEventListener('canplay', tryPlay, { once: true });
    }

    this.video.addEventListener('playing', markReady);

    this.video.addEventListener('pause', () => {
      // Retoma se pausar externamente (não no fim, porque loop)
      if (!this.video.ended && !document.fullscreenElement && !document.webkitFullscreenElement) {
        setTimeout(() => this.video.play().catch(() => {}), 100);
      }
    });

    if (this.playBtn) {
      this.playBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.video.play().then(markReady).catch(() => {});
      });
    }
  }

  /* ============================================
     FULLSCREEN on click
     ============================================ */

  setupVideoFullscreen() {
    if (!this.mediaWrap || !this.video) return;

    this.mediaWrap.addEventListener('click', (e) => {
      // Não ativa se click veio do botão play
      if (e.target.closest('.lcw-ritual-sticky__play')) return;
      this.openVideoFullscreen();
    });
  }

  openVideoFullscreen() {
    const video = this.video;
    if (!video) return;

    // iOS Safari — usa player nativo, melhor UX
    if (typeof video.webkitEnterFullscreen === 'function') {
      video.webkitEnterFullscreen();
      return;
    }

    // Standard Fullscreen API
    if (video.requestFullscreen) {
      video.requestFullscreen().catch(() => {});
    } else if (video.webkitRequestFullscreen) {
      video.webkitRequestFullscreen();
    } else if (video.msRequestFullscreen) {
      video.msRequestFullscreen();
    }
  }
}

/* ============================================
   Bootstrap
   ============================================ */

function initLcwRitualSticky(root) {
  if (!root || root.dataset.lcwRsInit === 'true') return;
  root.dataset.lcwRsInit = 'true';
  new LcwRitualSticky(root);
}

function initAllLcwRitualSticky() {
  document.querySelectorAll('.lcw-ritual-sticky').forEach(initLcwRitualSticky);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAllLcwRitualSticky);
} else {
  initAllLcwRitualSticky();
}

document.addEventListener('shopify:section:load', (e) => {
  const root = e.target.querySelector('.lcw-ritual-sticky');
  if (root) {
    root.dataset.lcwRsInit = 'false';
    initLcwRitualSticky(root);
  }
});
